import { useMemo, useState } from 'react';
import { BarChart3, ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const TABS = [
  { id: 'summary', label: 'Summary', icon: ShieldCheck },
  { id: 'coverage', label: 'Coverage', icon: BarChart3 },
  { id: 'reports', label: 'Reports', icon: TwinneeLogo },
];

function ScoreBar({ label, value }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-400">{label}</span>
        <span className="text-slate-200 tabular-nums">{pct}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
        <div className="h-full bg-violet-500/80 rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ReportList({ title, items }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-3">
      <p className="text-xs font-medium text-slate-300 mb-2">{title}</p>
      <ul className="text-xs text-slate-400 space-y-1 list-disc pl-4">
        {items.slice(0, 12).map((item, i) => (
          <li key={i}>
            {typeof item === 'string' ? item : JSON.stringify(item)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CurriculumOptimizationV3Panel({ data }) {
  const [tab, setTab] = useState('summary');
  const [reportsOpen, setReportsOpen] = useState(true);

  const scores = data?.completeness_scores || {};
  const readiness = data?.final_curriculum_readiness_score ?? 0;
  const coverageEntries = data?.coverage_report?.entries || [];

  const stats = useMemo(() => ({
    slidesBefore: data?.slide_statistics?.slides_before ?? '—',
    slidesAfter: data?.slide_statistics?.total_slides ?? data?.updated_slide_architecture?.total_slides ?? '—',
    newConcepts: (data?.new_concepts_added || []).length,
    removed: (data?.concepts_removed || []).length,
    merged: (data?.concepts_merged || []).length,
  }), [data]);

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-violet-500/25 bg-violet-950/20 p-4">
        <div className="flex items-center justify-between gap-3 mb-2">
          <span className="text-sm text-violet-200">Curriculum readiness</span>
          <span className="text-2xl font-bold text-violet-100 tabular-nums">{readiness}</span>
        </div>
        <p className="text-xs text-slate-400">{data.optimization_summary || 'Optimization complete.'}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-center">
          {[
            ['Slides before', stats.slidesBefore],
            ['Slides after', stats.slidesAfter],
            ['New concepts', stats.newConcepts],
            ['Merged', stats.merged],
          ].map(([label, val]) => (
            <div key={label} className="rounded-md bg-slate-900/60 px-2 py-1.5">
              <p className="text-[10px] text-slate-500">{label}</p>
              <p className="text-sm font-medium text-slate-200">{val}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-700/60 pb-1">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-t-md transition-colors',
              tab === id ? 'bg-slate-800 text-violet-200' : 'text-slate-500 hover:text-slate-300',
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'summary' ? (
        <div className="space-y-3">
          {Object.entries(scores).map(([k, v]) => (
            <ScoreBar
              key={k}
              label={k.replace(/_/g, ' ')}
              value={v}
            />
          ))}
        </div>
      ) : null}

      {tab === 'coverage' ? (
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {coverageEntries.map((e) => (
            <div
              key={e.concept_id || e.name}
              className="flex items-start justify-between gap-2 text-xs border-b border-slate-800 pb-2"
            >
              <div>
                <p className="text-slate-200">{e.name}</p>
                {e.notes ? <p className="text-slate-500 mt-0.5">{e.notes}</p> : null}
              </div>
              <span className="shrink-0 px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 tabular-nums">
                {e.coverage_score ?? '—'}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {tab === 'reports' ? (
        <div className="space-y-2">
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-slate-400"
            onClick={() => setReportsOpen((o) => !o)}
          >
            {reportsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Optimization reports
          </button>
          {reportsOpen ? (
            <div className="space-y-2">
              <ReportList title="Missing concepts" items={data.missing_concept_report} />
              <ReportList title="Hidden knowledge" items={data.hidden_concepts_report} />
              <ReportList title="Learning flow errors" items={data.learning_flow_errors} />
              <ReportList title="Slide merge recommendations" items={data.slide_merge_recommendations} />
              <ReportList title="Slide split recommendations" items={data.slide_split_recommendations} />
              {data.cognitive_load_report ? (
                <p className="text-xs text-slate-400 border border-slate-700/50 rounded-lg p-3">
                  {data.cognitive_load_report}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
